import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";

// Notification read-state actions. Spread into `actionsMutators` (actions.ts).
// Each enforces that the caller can only touch their own notification rows.
export const notificationActionMutators = {
  // Mark a single notification as read
  markNotificationRead: defineMutator(
    z.object({
      notificationId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const notification = await tx.run(
          zql.notification_events.where("id", args.notificationId).one(),
        );
        if (!notification) return;
        if (notification.recipient_user_id !== ctx.sub) {
          throw new Error("Unauthorized: can only read own notifications");
        }
      }
      await tx.mutate.notification_events.update({
        id: args.notificationId,
        read_at: Date.now(),
      });
    },
  ),

  // Mark all notifications for a channel as read
  markChannelNotificationsRead: defineMutator(
    z.object({
      userId: z.string(),
      channelId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.userId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const events = await tx.run(
        zql.notification_events
          .where("recipient_user_id", args.userId)
          .where("channel_id", args.channelId)
          .where("read_at", "IS", null),
      );
      const now = Date.now();
      for (const event of events) {
        await tx.mutate.notification_events.update({
          id: event.id,
          read_at: now,
        });
      }
    },
  ),

  // Mark all notifications as read for a user
  markAllNotificationsRead: defineMutator(
    z.object({
      userId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.userId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      const events = await tx.run(
        zql.notification_events
          .where("recipient_user_id", args.userId)
          .where("read_at", "IS", null),
      );
      const now = Date.now();
      for (const event of events) {
        await tx.mutate.notification_events.update({
          id: event.id,
          read_at: now,
        });
      }
    },
  ),
};
